import * as React from 'react';
import DatePickerInput, { DatePickerInputProps } from './DatePickerInput';
import { MinDateConst, MaxDateConst } from '../../utils/common';
import { canWalkWorkingDays } from '../timeline/utils';
import { Validator } from '../../../validation';
import { CalendarDataSet } from '../../../store/calendar/common';

type Props = DatePickerInputProps & {
    assigneeCalendars: CalendarDataSet[];
    useResourceCalendar: boolean;
};

const TaskStartDueDatePickerInput = ({ assigneeCalendars, useResourceCalendar, ...pickerProps }: Props) => {
    // Bug 17186: the capacity has to be part of the key. It is what decides whether a day is workable (US 15956
    // ID 11) but leaves no mark on the two fields below, so a resource on the default work week produces a calendar
    // byte-identical to the global one that getAssigneeCalendars stands in for an EMPTY assignee list. Removing that
    // resource therefore did not change the key, and the picker kept greying out the days it had no capacity on —
    // for a task with nobody assigned. The same collision hid the opposite case: assigning such a resource while the
    // panel is open left every day selectable.
    const calendarsKey = JSON.stringify(
        assigneeCalendars.map(_ => [_.workDayExpectedHrs, _.exceptionsHoursMap, _.capacityKey ?? '']));

    const restrictedDates = React.useMemo(() => {
        if (!useResourceCalendar || !assigneeCalendars.length || !canWalkWorkingDays(assigneeCalendars)) {
            return undefined;
        }
        const dates: Date[] = [];
        const current = new Date(MinDateConst);
        while (current <= MaxDateConst) {
            if (!current.isWorkingDay(...assigneeCalendars)) {
                dates.push(new Date(current));
            }
            current.setDate(current.getDate() + 1);
        }
        return dates;
    }, [calendarsKey, useResourceCalendar]);

    const validator = React.useMemo(() => {
        if (!pickerProps.validator) {
            return undefined;
        }
        return Validator.new().add(pickerProps.validator).customDate((value: Date) => {
            const year = value.getFullYear();
            const month = value.getMonth();
            const date = value.getDate();
            if (restrictedDates && value) {
                return !restrictedDates.some(d => d.getFullYear() === year && d.getMonth() === month && d.getDate() === date);
            }
            return true;
        }, 'Date is not available')
    }, [pickerProps.validator, restrictedDates]);

    return <DatePickerInput {...pickerProps} validator={validator?.build()} restrictedDates={restrictedDates} />;
};

export default TaskStartDueDatePickerInput;